import { readCsrfToken } from "@/lib/csrf"
import i18n from "@/i18n"
import type { ScaleSheetCharacterRow } from "@/lib/scale-sheet-api"
import type { PropScaleSheetPropRow } from "@/lib/prop-scale-sheet-api"

export type MixedScaleSheetCharacterRow = ScaleSheetCharacterRow
export type MixedScaleSheetPropRow = PropScaleSheetPropRow

export type MixedScaleSheetResponse = {
  ok: boolean
  status: string
  entity_ids_key: string
  image_url: string | null
  image_download_url?: string | null
  layout_meta: {
    figures?: {
      entity_id?: number
      entity_type?: string
      character_id?: number
      name: string
      height_cm: number
      x: number
      y: number
      w: number
      h: number
      scale_pose_url?: string | null
      hit_rect?: { x: number; y: number; w: number; h: number }
      image_width?: number
      image_height?: number
    }[]
    content_width_preview?: number
    px_per_cm_preview?: number
    characters?: { id: number; image_width?: number; image_height?: number }[]
    props?: { id: number; image_width?: number; image_height?: number }[]
  } | null
  scale_unit: string
  characters: MixedScaleSheetCharacterRow[]
  props: MixedScaleSheetPropRow[]
  error?: string
}

export type FetchMixedScaleSheetOptions = {
  editOnly?: boolean
  shotId?: number
  characterIds?: number[]
  propIds?: number[]
}

export async function fetchMixedScaleSheet(
  projectId: number,
  options: FetchMixedScaleSheetOptions = {},
): Promise<MixedScaleSheetResponse> {
  const params = new URLSearchParams()
  if (options.shotId != null && options.shotId > 0) {
    params.set("shot_id", String(options.shotId))
  } else {
    if (options.characterIds != null && options.characterIds.length > 0) {
      params.set("character_ids", options.characterIds.join(","))
    }
    if (options.propIds != null && options.propIds.length > 0) {
      params.set("prop_ids", options.propIds.join(","))
    }
  }
  if (options.editOnly) {
    params.set("edit_only", "1")
  }
  const res = await fetch(`/projects/${projectId}/workspace/mixed_scale_sheet?${params}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as MixedScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:common.mixedScaleSheetLoadError"))
  }
  return data
}

export type EnsureShotMixedScaleSheetResponse = {
  ok: boolean
  status: string
  entity_ids_key: string | null
  error?: string
}

export async function ensureShotMixedScaleSheet(
  projectId: number,
  shotId: number,
): Promise<Pick<EnsureShotMixedScaleSheetResponse, "status" | "entity_ids_key">> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/ensure_mixed_scale_sheet`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as EnsureShotMixedScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("shot:common.mixedScaleSheetLoadError"))
  }
  return { status: data.status, entity_ids_key: data.entity_ids_key }
}
